/**
 * @file MultiTenantNotificationTemplatesList.js
 * @description This page displays a list of multitenant notification templates. Templates are tied to a
 * single agency (or Global) and are made available to that agency's associated clients.
 */
import '../../page-list/PageList.css';
import styles from './MultiTenantNotificationTemplatesList.module.scss';
import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import NotFoundBox from '../../ui/no_data_indicators/NotFoundBox';
import Toast from '../../ui/toasts/Toast';
import Spinner from '../../ui/spinners/Spinner';
import { ModalContext, ModalTypes } from '../../../store/ModalContext';
import {
    FilterModal,
    useFilter,
    renderFilterIcon,
} from '../../helpers/FilterComponent';
import PaginationComponent from '../../helpers/PaginationComponent';
import {
    getNotificationTemplates,
    getNotificationTemplateById,
    createNotificationTemplate,
    updateNotificationTemplate,
    deleteNotificationTemplate,
    getNotificationCategories,
} from '../../../services/notificationTemplateService';
import {
    getClientNotificationTemplates,
    getClientNotificationTemplateById,
} from '../../../services/clientNotificationTemplateService';
import { getAgencies } from '../../../services/systemService';
import { getNameForDuplicate } from '../../../utils/utils';
import { useAuthContext } from '../../../store/AuthContext';
import { ROUTE_BUILDERS, ROUTES } from '../../../constants/routes';
import { SecurityAttributes, TenantTiers } from '../../../utils/data/enums';
import { getText } from '../../../i18n';

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const NOTIFICATION_STATUS_ENABLED = 1;
const NOTIFICATION_STATUS_DISABLED = 2;
const NOTIFICATION_STATUS_ACTIVE_LABEL = 'Active';

// The list endpoint returns a display-ready notificationStatus string (e.g. "Active"/"Inactive")
// rather than the numeric notificationStatusId used by the create/update payload.
const isTemplateEnabled = template =>
    'notificationStatus' in template
        ? template.notificationStatus === NOTIFICATION_STATUS_ACTIVE_LABEL
        : template.notificationStatusId === NOTIFICATION_STATUS_ENABLED;

const PAGE_SIZE = 10;

const getTemplateRowActionState = templateActionId => templateId =>
    templateActionId === templateId;

const MultiTenantNotificationTemplatesList = ({ isClientView = false }) => {
    const navigate = useNavigate();
    const { showModal } = useContext(ModalContext);
    const { hasAttribute } = useAuthContext();
    const isTemplateReadOnly = hasAttribute(
        SecurityAttributes.MultiTenantNotificationTemplatesReadOnly,
    );
    const isReadOnly = isClientView || isTemplateReadOnly;
    const canManageTemplates = !isClientView && !isTemplateReadOnly;

    const [templates, setTemplates] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [templateActionId, setTemplateActionId] = useState(null);
    const [rowsClicked, setRowsClicked] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: 'name',
        direction: SortDirections.ASCENDING,
    });
    const [baseFilteredData, setBaseFilteredData] = useState([]);
    const [filteredData, applyFilter, clearAllFilters] =
        useFilter(baseFilteredData);
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');

    const getScopeLabel = template => {
        if (template.agencyId === 0) return 'Global';
        return (
            agencies.find(a => a.id === template.agencyId)?.name || 'Unknown'
        );
    };

    // The list endpoint returns a display-ready notificationCategory name; fall back to resolving
    // notificationCategoryId via the categories lookup for other/older payload shapes.
    const getCategoryLabel = template => {
        if (template.notificationCategory) return template.notificationCategory;
        return (
            categories.find(c => c.id === template.notificationCategoryId)
                ?.name || '-'
        );
    };

    const fetchTemplates = async () => {
        setIsLoading(true);
        try {
            const payload = isClientView
                ? await getClientNotificationTemplates()
                : await getNotificationTemplates();

            const viewModels = Array.isArray(payload)
                ? payload
                : payload?.viewModels || [];

            setTemplates(viewModels);
        } catch (error) {
            console.error('Failed to fetch notification templates', error);
            Toast.error(getText('msg_6d9c0e7f25b9'));
            setTemplates([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAgencies = async () => {
        try {
            const fetchedAgencies = await getAgencies();
            setAgencies(fetchedAgencies.viewModels || []);
        } catch (error) {
            console.error('Failed to fetch agencies', error);
            Toast.error(getText('msg_27ded7882fa5'));
        }
    };

    const fetchCategories = async () => {
        try {
            const fetchedCategories = await getNotificationCategories();
            setCategories(fetchedCategories || []);
        } catch (error) {
            console.error('Failed to fetch notification categories', error);
        }
    };

    useEffect(() => {
        fetchTemplates();
        fetchAgencies();
        fetchCategories();
    }, []);

    useEffect(() => {
        const searchFiltered = templates.filter(t =>
            t.name?.toLowerCase().includes(searchTerm.toLowerCase()),
        );

        const compareAlphabetically = (left, right) =>
            (left || '').localeCompare(right || '', undefined, {
                sensitivity: 'base',
            });

        const sorted = [...searchFiltered].sort((a, b) => {
            const key = sortConfig.key;
            const dir =
                sortConfig.direction === SortDirections.ASCENDING ? 1 : -1;

            if (key === 'scope') {
                const aScopeLabel = (getScopeLabel(a) || '').toString();
                const bScopeLabel = (getScopeLabel(b) || '').toString();

                const aIsGlobal = a.agencyId === 0;
                const bIsGlobal = b.agencyId === 0;

                if (aIsGlobal !== bIsGlobal) {
                    return (aIsGlobal ? -1 : 1) * dir;
                }

                return compareAlphabetically(aScopeLabel, bScopeLabel) * dir;
            }

            const getValue = item => {
                switch (key) {
                    case 'name':
                        return (item.name || '').toLowerCase();
                    case 'bronze':
                        return item.availableTierIds?.includes(
                            TenantTiers.Bronze,
                        )
                            ? 1
                            : 0;
                    case 'base':
                        return item.availableTierIds?.includes(TenantTiers.Base)
                            ? 1
                            : 0;
                    case 'enabled':
                        return isTemplateEnabled(item) ? 1 : 0;
                    case 'category':
                        return getCategoryLabel(item).toLowerCase();
                    default:
                        return (item[key] || '').toString().toLowerCase();
                }
            };

            const va = getValue(a);
            const vb = getValue(b);

            if (typeof va === 'number' && typeof vb === 'number') {
                return (va - vb) * dir;
            }
            if (va < vb) return -1 * dir;
            if (va > vb) return 1 * dir;
            return 0;
        });

        setBaseFilteredData(sorted);
    }, [templates, searchTerm, sortConfig, agencies]);

    const handleSearch = event => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const toggleSort = key => {
        setSortConfig(prev =>
            prev.key === key
                ? {
                      key,
                      direction:
                          prev.direction === SortDirections.ASCENDING
                              ? SortDirections.DESCENDING
                              : SortDirections.ASCENDING,
                  }
                : { key, direction: SortDirections.ASCENDING },
        );
    };

    const renderSortCaret = key => (
        <svg width={11} height={6} viewBox="0 0 11 6" fill="none">
            <path
                transform={
                    sortConfig.key === key &&
                    sortConfig.direction === SortDirections.DESCENDING
                        ? 'rotate(180 5.25 3)'
                        : ''
                }
                d="M1 0.75L5.25 5.25L9.5 0.75"
                stroke="#2F2B43"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    const handlerRowClicked = templateId => {
        setRowsClicked(prevSelection =>
            prevSelection.includes(templateId)
                ? prevSelection.filter(id => id !== templateId)
                : [...prevSelection, templateId],
        );
    };

    const openModal = column => setActiveModal(column);
    const closeModal = () => setActiveModal(null);

    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));

        if (column === 'scope') {
            const mappedIds = (selectedOptions || []).map(opt => {
                if (opt === 'Global') return 0;
                const match = agencies.find(a => a.name === opt);
                return match ? match.id : opt;
            });

            applyFilter('agencyId', mappedIds);
            return;
        }

        if (column === 'category') {
            applyFilter('notificationCategory', selectedOptions);
            return;
        }

        applyFilter(column, selectedOptions);
    };

    const clearFilters = () => {
        setSelections({});
        setSearchTerm('');
        clearAllFilters();
        setCurrentPage(1);
    };

    const getOptions = column => {
        const filtersWithoutCurrent = { ...selections };
        delete filtersWithoutCurrent[column];

        const filtered = templates.filter(item =>
            Object.entries(filtersWithoutCurrent).every(([key, values]) => {
                if (values.length === 0) return true;
                if (key === 'scope')
                    return values.includes(getScopeLabel(item));
                if (key === 'category')
                    return values.includes(getCategoryLabel(item));
                return values.includes(item[key]);
            }),
        );

        if (column === 'scope') {
            return Array.from(
                new Set(filtered.map(item => getScopeLabel(item))),
            )
                .filter(Boolean)
                .sort((a, b) => {
                    const first = (a || '').toString().toLowerCase();
                    const second = (b || '').toString().toLowerCase();

                    if (first === 'global' && second !== 'global') return -1;
                    if (first !== 'global' && second === 'global') return 1;

                    return first.localeCompare(second);
                });
        }

        if (column === 'category') {
            return Array.from(
                new Set(filtered.map(item => getCategoryLabel(item))),
            )
                .filter(Boolean)
                .sort();
        }

        return Array.from(new Set(filtered.map(item => item[column])))
            .filter(Boolean)
            .sort();
    };

    const onDeleteTemplates = async () => {
        const results = await Promise.allSettled(
            rowsClicked.map(id => deleteNotificationTemplate(id)),
        );

        results.forEach((result, index) => {
            if (result.status === 'rejected') {
                console.error(
                    `Failed to delete notification template ${rowsClicked[index]}`,
                    result.reason,
                );
            }
        });

        const succeeded = rowsClicked.filter((_, index) => {
            return results[index].status === 'fulfilled';
        });

        const failed = rowsClicked.filter((_, index) => {
            return results[index].status === 'rejected';
        });

        if (succeeded.length > 0) {
            setTemplates(prev => prev.filter(t => !succeeded.includes(t.id)));
        }

        if (failed.length === 0) {
            Toast.success(
                `${succeeded.length > 1 ? 'Templates' : 'Template'} deleted successfully.`,
            );
            setRowsClicked([]);
            return;
        }

        if (succeeded.length > 0) {
            Toast.error(
                `${succeeded.length} template(s) deleted, ${failed.length} failed.`,
            );
            setRowsClicked(failed);
            return;
        }

        Toast.error(getText('msg_5c8b9d6e14a8'));
    };

    const onDuplicateTemplates = async () => {
        const currentNames = templates.map(t => t.name).filter(Boolean);
        const created = [];
        const failed = [];

        for (const id of rowsClicked) {
            try {
                const original = await getNotificationTemplateById(id);
                if (!original)
                    throw new Error(`Template not found for id ${id}`);

                const { id: _omit, ...payload } = original;
                const newName = getNameForDuplicate(
                    original.name,
                    currentNames,
                );
                payload.name = newName;

                const createdItem = await createNotificationTemplate(payload);
                created.push(createdItem);
                currentNames.push(newName);
            } catch (err) {
                console.error(`Failed to duplicate template id ${id}`, err);
                failed.push(id);
            }
        }

        if (created.length > 0) {
            setTemplates(prev => [...created, ...prev]);
        }

        if (failed.length === 0) {
            Toast.success(
                `${created.length > 1 ? 'Templates' : 'Template'} duplicated successfully.`,
            );
            setRowsClicked([]);
        } else {
            Toast.error(`Failed to duplicate ${failed.length} template(s).`);
        }
    };

    const onCreateFromTemplate = async (
        templateId,
        scheduleAfterSave = false,
    ) => {
        if (templateActionId !== null) return;

        setTemplateActionId(templateId);
        try {
            const template = isClientView
                ? await getClientNotificationTemplateById(null, templateId)
                : await getNotificationTemplateById(templateId);
            if (!template) throw new Error('Template not found');

            navigate(ROUTES.MESSAGES_ADD, {
                state: {
                    notificationTemplate: {
                        name: template.name,
                        subject: template.subject || '',
                        htmlBody: template.htmlBody || '',
                        plainTextBody: template.plainTextBody || '',
                        pushBody: template.pushBody || '',
                        pushDestinationTypeId:
                            template.pushDestinationTypeId || null,
                        pushDestinationValue:
                            template.pushDestinationValue || null,
                        notificationCategoryId:
                            template.notificationCategoryId || null,
                    },
                    scheduleAfterSave,
                },
            });
        } catch (error) {
            console.error('Failed to create notification from template', error);
            Toast.error('Failed to load the selected template.');
        } finally {
            setTemplateActionId(null);
        }
    };

    const onToggle = async id => {
        try {
            const response = await getNotificationTemplateById(id);
            if (!response) throw new Error('Template not found');

            const updatedTemplate = { ...response };
            delete updatedTemplate.createdBy;
            delete updatedTemplate.createdAt;
            delete updatedTemplate.updatedBy;
            delete updatedTemplate.updatedAt;
            updatedTemplate.notificationStatusId =
                response.notificationStatusId === NOTIFICATION_STATUS_ENABLED
                    ? NOTIFICATION_STATUS_DISABLED
                    : NOTIFICATION_STATUS_ENABLED;

            const isNowEnabled =
                updatedTemplate.notificationStatusId ===
                NOTIFICATION_STATUS_ENABLED;

            await updateNotificationTemplate(updatedTemplate);
            await fetchTemplates();

            Toast.success(
                `${updatedTemplate.name} was ${isNowEnabled ? 'enabled' : 'disabled'} successfully`,
            );
        } catch (err) {
            console.error('Failed to toggle template enable status', err);
            Toast.error(getText('msg_9b2a253076c9'));
        }
    };

    const rowAction = action => {
        if (isReadOnly) {
            Toast.error(getText('msg_ababfe94a870'));
            return;
        }

        if (!rowsClicked.length) {
            Toast.error(getText('msg_b241519270fe'));
            return;
        }

        if (action === 'delete') {
            showModal({
                type: ModalTypes.DELETE_CONFIRMATION,
                title: getText('msg_295e6a3b8175'),
                text: getText('msg_3a6f7b4c9286'),
                onYes: () => onDeleteTemplates(),
                onNo: () => {},
                noButtonText: getText('msg_77dfd2135f4d'),
                yesButtonText: getText('msg_f6fdbe48dc54'),
            });
            return;
        }

        if (action === 'duplicate') {
            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_5ebb09c8ea76'),
                subtitle: '',
                text: `This action will create copies of the ${rowsClicked.length > 1 ? 'selected templates' : 'selected template'}.`,
                onYes: () => onDuplicateTemplates(),
                onNo: () => {},
                noButtonText: getText('msg_77dfd2135f4d'),
                yesButtonText: getText('msg_972d57379db3'),
            });
        }
    };

    const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));
    const pagedData = filteredData.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE,
    );

    if (isLoading) {
        return <Spinner width={50} className={styles.loadingSpinner} />;
    }

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    {isClientView ? (
                        <div className="d-flex flex-column mb-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="float-start d-flex">
                                    <p className="page-title card-title ms-4 me-5">
                                        {getText('msg_753a22b2eb61')}
                                    </p>
                                </div>
                            </div>

                            <div
                                id="notification-templates-panel"
                                role="tabpanel"
                                aria-label="Notification templates"
                            >
                                <p className="ing-portals-subtitle ms-4 mb-2">
                                    {getText('msg_46cbd2d34ee0')}
                                    <br />
                                    {getText('msg_5d77b298b830')}
                                </p>
                            </div>

                            <div
                                className={styles.tabList}
                                role="tablist"
                                aria-label="Notification views"
                            >
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected="false"
                                    aria-controls="custom-notifications-panel"
                                    className={styles.tabItem}
                                    onClick={() => navigate(ROUTES.MESSAGES)}
                                >
                                    Custom
                                </button>

                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected="true"
                                    aria-controls="notification-templates-panel"
                                    className={`${styles.tabItem} ${styles.active}`}
                                >
                                    Templates
                                </button>
                            </div>

                            <br />
                            <br />

                            <div
                                className={`${styles.toolbarRow} ms-4 me-4 mb-3`}
                            >
                                <div className={styles.searchWrapper}>
                                    <input
                                        type="text"
                                        className={styles.searchInput}
                                        placeholder={getText(
                                            'msg_731483f48c21',
                                        )}
                                        value={searchTerm}
                                        onChange={handleSearch}
                                    />
                                    <span className={styles.searchIcon}>
                                        <i className="bi bi-search"></i>
                                    </span>
                                </div>

                                <div className={styles.toolbarActions}>
                                    <span
                                        type="button"
                                        onClick={clearFilters}
                                        className="ing-text-buttons d-flex align-items-center"
                                    >
                                        <i className="bi bi-arrow-counterclockwise me-2" />
                                        {getText('msg_381c4bf1d409')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div className="float-start d-flex flex-direction-column">
                                <p className="page-title card-title ms-4 me-5">
                                    {getText('msg_a4c1e8f2b310')}
                                </p>
                                <p
                                    className="ing-portals-subtitle mt-1"
                                    style={{
                                        marginTop: '10px',
                                        maxWidth: '600px',
                                    }}
                                >
                                    {getText('msg_b2d9f6a1c407')}
                                </p>
                            </div>
                            <div
                                className="d-flex align-items-center"
                                style={{ gap: '24px' }}
                            >
                                <span
                                    type="button"
                                    onClick={clearFilters}
                                    className="ing-text-buttons d-flex align-items-center"
                                >
                                    <i className="bi bi-arrow-counterclockwise me-2" />
                                    {getText('msg_381c4bf1d409')}
                                </span>

                                {canManageTemplates && (
                                    <>
                                        <span
                                            type="button"
                                            onClick={() =>
                                                rowAction('duplicate')
                                            }
                                            className="ing-text-buttons d-flex align-items-center"
                                        >
                                            <i className="bi bi-copy me-2" />
                                            {getText('msg_972d57379db3')}
                                        </span>
                                        <span
                                            type="button"
                                            onClick={() => rowAction('delete')}
                                            className="ing-text-buttons d-flex align-items-center"
                                        >
                                            <i className="bi bi-trash3 me-2" />
                                            {getText('msg_f6fdbe48dc54')}
                                        </span>
                                        <span
                                            onClick={() =>
                                                navigate(
                                                    ROUTES.NOTIFICATION_TEMPLATES_NEW,
                                                )
                                            }
                                            type="button"
                                            className="button-base ing-button-primary-left align-middle me-4"
                                        >
                                            <i className="bi bi-plus-lg me-1" />
                                            {getText('msg_61cc55aa0453')}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {!isClientView && (
                        <div className="page-title card-title ms-4 me-5 mb-5">
                            <div className={styles.searchWrapper}>
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder={getText('msg_c3e0a7b2d519')}
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                                <span className={styles.searchIcon}>
                                    <i className="bi bi-search"></i>
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="table-responsive-xxl w-100">
                        <table className="table table-hover align-middle">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    {!isClientView && (
                                        <th className="text-center">
                                            <svg
                                                width="19"
                                                height="19"
                                                viewBox="0 0 19 19"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M5.2 9.3H12.8"
                                                    stroke="#0070FF"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                                <rect
                                                    x="0.5"
                                                    y="0.5"
                                                    width="17"
                                                    height="17"
                                                    rx="4"
                                                    stroke="#0070FF"
                                                />
                                            </svg>
                                        </th>
                                    )}
                                    <th
                                        style={
                                            isClientView
                                                ? {
                                                      paddingLeft: '30px',
                                                  }
                                                : null
                                        }
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_709a23220f2c')}
                                        </span>
                                        <span
                                            onClick={() => toggleSort('name')}
                                            className={`mx-2 ${styles.cursorPointer}`}
                                        >
                                            {renderSortCaret('name')}
                                        </span>
                                        <span
                                            onClick={() => openModal('name')}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['name']?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'name' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>

                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_55f8ebc805e6')}
                                        </span>
                                    </th>

                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_a3c686e711e4')}
                                        </span>
                                        <span
                                            onClick={() =>
                                                toggleSort('category')
                                            }
                                            className={`px-2 ${styles.cursorPointer}`}
                                        >
                                            {renderSortCaret('category')}
                                        </span>
                                        <span
                                            onClick={() =>
                                                openModal('category')
                                            }
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['category']?.length >
                                                    0,
                                            )}
                                        </span>
                                        {activeModal === 'category' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>

                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_073c4e1f6953')}
                                        </span>
                                        <span
                                            onClick={() => toggleSort('scope')}
                                            className={`px-2 ${styles.cursorPointer}`}
                                        >
                                            {renderSortCaret('scope')}
                                        </span>
                                        <span
                                            onClick={() => openModal('scope')}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['scope']?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'scope' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>

                                    {!isClientView && (
                                        <>
                                            <th className="text-center">
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_af3102527f03',
                                                    )}
                                                </span>
                                            </th>
                                            <th className="text-center">
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_077fe9c54ed5',
                                                    )}
                                                </span>
                                            </th>
                                            <th className="text-center">
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_20063ad90532',
                                                    )}
                                                </span>
                                            </th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {pagedData.map(template => (
                                    <tr
                                        key={template.id}
                                        className={
                                            rowsClicked.includes(template.id)
                                                ? 'clicked-class'
                                                : ''
                                        }
                                    >
                                        {!isClientView && (
                                            <td className="text-center">
                                                <input
                                                    onClick={() =>
                                                        handlerRowClicked(
                                                            template.id,
                                                        )
                                                    }
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={rowsClicked.includes(
                                                        template.id,
                                                    )}
                                                    disabled={isReadOnly}
                                                    readOnly={isReadOnly}
                                                />
                                            </td>
                                        )}

                                        <td
                                            className={
                                                styles.itemPageNameWithHover
                                            }
                                            style={
                                                isClientView
                                                    ? {
                                                          paddingLeft: '30px',
                                                      }
                                                    : null
                                            }
                                        >
                                            <span
                                                onClick={() =>
                                                    navigate(
                                                        isClientView
                                                            ? ROUTE_BUILDERS.messagesTemplatesView(
                                                                  template.id,
                                                              )
                                                            : ROUTE_BUILDERS.notificationTemplatesView(
                                                                  template.id,
                                                              ),
                                                    )
                                                }
                                                className={styles.cursorPointer}
                                            >
                                                {template.name}
                                            </span>
                                        </td>

                                        <td className="item-page-changed-by">
                                            {template.description || '-'}
                                        </td>

                                        <td className="item-page-changed-by">
                                            {getCategoryLabel(template)}
                                        </td>

                                        <td className="item-page-changed-by">
                                            {getScopeLabel(template)}
                                        </td>

                                        {!isClientView && (
                                            <>
                                                <td className="text-center">
                                                    {template.availableTierIds?.includes(
                                                        TenantTiers.Bronze,
                                                    )
                                                        ? 'Y'
                                                        : '-'}
                                                </td>
                                                <td className="text-center">
                                                    {template.availableTierIds?.includes(
                                                        TenantTiers.Base,
                                                    )
                                                        ? 'Y'
                                                        : '-'}
                                                </td>
                                                <td className="text-center">
                                                    <div className="form-switch">
                                                        <input
                                                            onChange={() =>
                                                                onToggle(
                                                                    template.id,
                                                                )
                                                            }
                                                            className="form-check-input custom-switch-input"
                                                            type="checkbox"
                                                            checked={isTemplateEnabled(
                                                                template,
                                                            )}
                                                            disabled={
                                                                isReadOnly
                                                            }
                                                        />
                                                    </div>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {(templates == null || templates.length < 1) && (
                            <NotFoundBox
                                backgroundColor="#f0f4ff"
                                title={getText('msg_469b72190312')}
                            />
                        )}

                        {totalPages > 1 && (
                            <div className="mt-auto">
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MultiTenantNotificationTemplatesList;
